import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { eachDayOfInterval, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCalendar } from '../calendarContext';
import BookItOrCreate from '../bookItOrCreate';
import { calendarNow, eventOnCalendarDay } from '../../../../../../platform/util/calendarTimezone';
import { calendarMonthRange, calendarParts, ETHIOPIAN_MONTHS, formatCalendarDate } from '../../../../../../platform/util/calendarSystem';
import { formatClockText } from '../../../../../../platform/util/clockSystem';
import { appointmentColors } from '../shared/appointmentColors';
import { formatDuration } from '../shared/formatDuration';
import { appointmentConflicts } from '../shared/appointmentConflicts';

const eventTitle = ({ event, bookingType }) => event.quickTitle || event.title || event.summary || event.bookingTypeName || bookingType.title || event.inviteeName || 'Event';

export default function CompactAgenda({ date, events, monthAgenda = false }) {
  const { myBookingTypes, displayTimezone, displayCalendar, displayClock } = useCalendar();
  const booking = useRef(null);
  const [conflictItem, setConflictItem] = useState(null);
  const scheduleRef = useRef(null);
  const selectedDayRef = useRef(null);
  const positionedDate = useRef(null);
  const userScrolled = useRef(false);
  const lastScrollTop = useRef(0);
  const today = format(calendarNow(displayTimezone), 'yyyy-MM-dd');
  const selectedDay = format(date, 'yyyy-MM-dd');
  const range = calendarMonthRange(selectedDay, displayCalendar);
  useLayoutEffect(() => {
    if (!monthAgenda) return;
    const scroller = scheduleRef.current?.closest('[data-calendar-scroll]');
    const anchor = selectedDayRef.current;
    if (!scroller || !anchor) return;
    if (positionedDate.current !== selectedDay) userScrolled.current = false;
    positionedDate.current = selectedDay;
    if (userScrolled.current) return;
    const position = () => {
      if (userScrolled.current) return;
      scheduleRef.current.style.paddingBottom = '';
      const target = Math.max(0, scroller.scrollTop + anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top);
      // Leave enough room below the last date to put the selection at the top, even
      // when there are no later appointments. Only move this owned scroller.
      const missingSpace = target - Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      if (missingSpace > 0) scheduleRef.current.style.paddingBottom = `${missingSpace + 16}px`;
      scroller.scrollTop = target;
      lastScrollTop.current = scroller.scrollTop;
    };
    // Initial bookings can arrive in several batches. Keep the selection in
    // place until the user takes control, including after the first layout.
    position();
    const frame = requestAnimationFrame(position);
    const takeControl = () => { userScrolled.current = true; };
    const onScroll = () => {
      if (Math.abs(scroller.scrollTop - lastScrollTop.current) > 1) takeControl();
    };
    const onKeyDown = event => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) takeControl();
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('wheel', takeControl, { passive: true });
    scroller.addEventListener('touchstart', takeControl, { passive: true });
    scroller.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('wheel', takeControl);
      scroller.removeEventListener('touchstart', takeControl);
      scroller.removeEventListener('keydown', onKeyDown);
    };
  }, [monthAgenda, selectedDay, events]);
  const days = monthAgenda ? eachDayOfInterval({ start: new Date(`${range.start}T12:00:00`), end: new Date(`${range.end}T12:00:00`) }) : [date];
  const clock = value => formatClockText(formatInTimeZone(value, displayTimezone, 'h:mm a'), displayClock);
  const entries = events.map(event => {
    const startTime = new Date(event.time);
    const bookingType = (myBookingTypes || []).find(type => String(type.id) === String(event.bookingTypeId)) || {
      title: event.quickTitle || event.title || event.summary || event.bookingTypeName || 'Event',
      color: String(event.source).toLowerCase().includes('google') ? '#34a853' : '#0ea5e9',
      length: event.duration || 60,
    };
    const end = new Date(event.endTime);
    const endTime = end > startTime ? end : new Date(startTime.getTime() + (event.duration || bookingType.length || 60) * 60000);
    return { event, startTime, endTime, bookingType };
  }).filter(item => !Number.isNaN(item.startTime.getTime()))
    .sort((a, b) => Number(Boolean(b.event.isAllDay)) - Number(Boolean(a.event.isAllDay)) || a.startTime - b.startTime);
  const conflicts = appointmentConflicts(entries);
  const conflictMatches = conflictItem ? appointmentConflicts([conflictItem, ...entries.filter(item => item.event !== conflictItem.event)]).get(conflictItem) : [];
  const dateTime = value => `${formatCalendarDate(formatInTimeZone(value, displayTimezone, 'yyyy-MM-dd'), displayCalendar)} · ${clock(value)}`;
  const groups = days.map(day => ({ day, items: entries.filter(item => eventOnCalendarDay(item.event, day, displayTimezone)) }))
    .filter(group => !monthAgenda || group.items.length || format(group.day, 'yyyy-MM-dd') === selectedDay);
  return <Box ref={scheduleRef} aria-label={monthAgenda ? 'Monthly schedule' : 'Daily schedule'} sx={{ px: 1, pb: 2, bgcolor: 'background.paper' }}>
    {!groups.length && <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No events this month</Typography>}
    {groups.map(({ day, items }) => {
      const key = format(day, 'yyyy-MM-dd');
      const parts = calendarParts(key, displayCalendar);
      const label = displayCalendar === 'ethiopic' ? `${ETHIOPIAN_MONTHS[parts.month - 1]} ${parts.day}` : format(day, 'MMM d');
      return <Box key={key} ref={key === selectedDay ? selectedDayRef : undefined} component="section" aria-label={label} sx={{ pb: 1 }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', display: 'flex', alignItems: 'baseline', gap: 1, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}>
          <Typography component="h2" sx={{ fontSize: 13, fontWeight: 500, color: key === today ? 'primary.main' : 'text.primary' }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary">{format(day, 'EEEE')}</Typography>
          {key === today && <Typography variant="caption" color="primary.main" sx={{ ml: 'auto', fontSize: 11, px: 0.75, borderRadius: 1, bgcolor: 'action.hover' }}>Today</Typography>}
        </Box>
        {!items.length && <Typography color="text.secondary" variant="body2" sx={{ pl: '72px', py: 1 }}>No events</Typography>}
        {items.map((item, index) => {
          const { event, bookingType, startTime, endTime } = item;
          const title = eventTitle(item);
          const overlaps = conflicts.get(item);
          return <Box key={`${event.id || event.googleEventId || index}-${index}`} sx={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', columnGap: 1, mb: 1 }}>
            <Box sx={{ textAlign: 'left', pt: 1, fontSize: 11, fontWeight: 400, lineHeight: 1.45, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {event.isAllDay ? 'All day' : <>
                <Box>{clock(startTime)}</Box>
                <Box sx={{ mt: 0.5, fontSize: 10, overflowWrap: 'anywhere' }}>{formatDuration((endTime - startTime) / 60000)}</Box>
              </>}
            </Box>
            <Box sx={{ minWidth: 0, position: 'relative' }}>
            <ButtonBase aria-label={title} onClick={() => booking.current?.('update', [item])} sx={{ display: 'block', textAlign: 'left', width: '100%', minHeight: 56, p: 1.25, pr: overlaps.length ? 5.5 : 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', borderLeft: '2px solid', borderLeftColor: event.color || bookingType.color || '#0ea5e9', bgcolor: theme => appointmentColors(theme).background, color: 'text.primary', overflowWrap: 'anywhere', '&:hover': { bgcolor: 'action.selected' }, '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
              <Typography component="span" sx={{ display: 'block', fontSize: 13, fontWeight: 400, lineHeight: 1.45 }}>{title}</Typography>
              {event.inviteeName && event.inviteeName !== title && <Typography component="span" variant="caption" color="text.secondary">{event.inviteeName}</Typography>}
            </ButtonBase>
            {overlaps.length > 0 && <Tooltip title="Time overlap">
              <IconButton aria-label={`Show time conflicts for ${title}`} onClick={() => setConflictItem(item)} sx={{ position: 'absolute', right: 2, top: 6, width: 40, height: 44, color: theme => theme.palette.mode === 'dark' ? '#fcd34d' : '#8a4b08', '&:focus-visible': { outline: '2px solid', outlineColor: 'warning.main' } }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>}
            </Box>
          </Box>;
        })}
      </Box>;
    })}
    <Dialog open={Boolean(conflictItem)} onClose={() => setConflictItem(null)} fullWidth maxWidth="xs" aria-labelledby="schedule-conflict-title">
      <DialogTitle id="schedule-conflict-title" sx={{ fontSize: 17, fontWeight: 500 }}>Overlapping appointments</DialogTitle>
      <DialogContent dividers>
        {conflictItem && <Typography sx={{ fontSize: 13, mb: 1 }}>{eventTitle(conflictItem)} · {clock(conflictItem.startTime)} – {clock(conflictItem.endTime)}</Typography>}
        <List disablePadding>{conflictMatches.map((item, index) => <ListItemButton key={index} sx={{ borderRadius: 1, px: 1 }} onClick={() => { setConflictItem(null); booking.current?.('update', [item]); }}>
          <ListItemText primary={eventTitle(item)} secondary={`${dateTime(item.startTime)} – ${dateTime(item.endTime)}`} primaryTypographyProps={{ fontSize: 13, fontWeight: 400 }} secondaryTypographyProps={{ fontSize: 11 }} />
        </ListItemButton>)}</List>
        <Typography variant="caption" color="text.secondary">Based on timed appointments visible in this calendar.</Typography>
      </DialogContent>
      <DialogActions><Button onClick={() => setConflictItem(null)}>Close</Button></DialogActions>
    </Dialog>
    <BookItOrCreate triggerDialog={handler => { booking.current = handler; }} />
  </Box>;
}
